import { t } from "@lingui/macro";
import {
  type ProjectsWipFilterOptionsQuery,
  type ProjectsWipSummaryQuery,
  type ProjectsWipTableDataQuery,
  SortOrder,
  SqlOperator,
  WipOrderByColumnEnum,
  WipsWhereColumn,
} from "@src/__generated__/graphql";
import { WipPeriodsStore } from "@src/components/ui-kit/MonthPicker/WipPeriodsStore";
import type { AppStore } from "@src/stores/AppStore";
import {
  commonQueryVariables,
  commonSearchParams,
} from "@src/utils/apolloHelpers";
import { Filter, Filters } from "@src/utils/components/filters/models";
import { can } from "@src/utils/components/permissions";
import { OrderBy } from "@src/utils/components/sorting/OrderBy";
import map from "@src/utils/map-to-options";
import { PaginationState } from "@src/utils/mobx/states/PaginationState";
import { action, computed, makeObservable, observable } from "mobx";
import { ProjectWip } from "../models";

export class Store {
  @observable searchTerm = "";

  pagination = new PaginationState("projectsWip");

  orderBy = new OrderBy<WipOrderByColumnEnum>([
    { column: WipOrderByColumnEnum.ProjectCode, order: SortOrder.Asc },
  ]);

  where = new Filters<WipsWhereColumn>(
    [
      new Filter({
        column: WipsWhereColumn.BrandId,
        operator: SqlOperator.In,
        title: t`Client/Brand`,
        tagTitle: t`Brand`,
        options: [],
        sectioned: true,
        hasSelectAllOption: true,
      }),
      new Filter({
        column: WipsWhereColumn.ProjectManagerId,
        operator: SqlOperator.In,
        title: t`Project Manager`,
        options: [],
        hasSelectAllOption: true,
      }),
      new Filter({
        column: WipsWhereColumn.OtherProjectManagerId,
        operator: SqlOperator.In,
        title: t`Other Project Manager`,
        options: [],
        hasSelectAllOption: true,
      }),
      new Filter({
        column: WipsWhereColumn.TeamId,
        operator: SqlOperator.In,
        title: t`Team`,
        options: [],
      }),
      new Filter({
        column: WipsWhereColumn.Status,
        operator: SqlOperator.In,
        title: t`Status`,
        options: [
          {
            label: t`Open`,
            value: "open",
          },
          {
            label: t`Closed`,
            value: "closed",
          },
        ],
      }),
    ],
    {
      onChange: this.pagination.resetPage,
      tableKey: "project-wip",
      orderBy: this.orderBy,
    },
  );

  wipPeriodsStore: WipPeriodsStore = new WipPeriodsStore();

  @observable.ref projectsWip: ProjectWip[] = [];
  refetchProjectsWip: () => void = () => {};

  @observable.ref summary?: ProjectsWipSummaryQuery["wipProjectsPeriodSummary"];
  refetchSummary: () => void = () => {};

  constructor(appStore: AppStore) {
    makeObservable(this);
    // Default active tab: 'My'
    this.projectManagerFilter.setValue(appStore.authStore.user?.id!);

    // Add new status option: 'Locked'
    if (can(["wip_lock_own", "wip_lock_team", "wip_lock_all"])) {
      const statusFilter = this.where.filtersByColumn.get(
        WipsWhereColumn.Status,
      )!;

      statusFilter.setOptions([
        ...statusFilter.options,
        {
          label: t`Locked`,
          value: "locked",
        },
      ]);
    }
  }

  get projectManagerFilter() {
    return this.where.filtersByColumn.get(WipsWhereColumn.ProjectManagerId)!;
  }

  @computed get searchParams() {
    const params = commonSearchParams(this);

    return {
      ...params,
      period: this.wipPeriodsStore.currentPeriod?.id ?? "",
    };
  }

  @computed get queryParams() {
    const vars = commonQueryVariables(this);

    return {
      ...vars,
      filters: {
        period_id: this.wipPeriodsStore.currentPeriod?.id,
        ...vars.filters,
      },
    };
  }

  @action
  setFilterOptions(src: ProjectsWipFilterOptionsQuery) {
    const pmOptions = map.projectManagers.toOptions(
      src.projectManagerSimpleMap,
    );
    this.where.filtersByColumn
      .get(WipsWhereColumn.ProjectManagerId)
      ?.setOptions(pmOptions);
    this.where.filtersByColumn
      .get(WipsWhereColumn.OtherProjectManagerId)
      ?.setOptions(pmOptions);

    this.where.filtersByColumn
      .get(WipsWhereColumn.BrandId)
      ?.setOptions(map.clients.toOptions(src.clientsSimpleMap));

    this.where.filtersByColumn
      .get(WipsWhereColumn.TeamId)
      ?.setOptions(map.toOptions(src.teamSimpleMap, "name"));

    this.wipPeriodsStore.setOptions(src.wipPeriodsMap);
  }

  @action
  setSummary(src: ProjectsWipSummaryQuery) {
    this.summary = src.wipProjectsPeriodSummary;
  }

  @action
  setTableData(src: ProjectsWipTableDataQuery) {
    if (!src.projectsWithPeriods) return;

    this.pagination.setFromPaginatorInfo(src.projectsWithPeriods.paginatorInfo);
    this.projectsWip = src.projectsWithPeriods.data.map(
      (i) => new ProjectWip(i),
    );
  }
}
